import { KeyboardArrowDown, KeyboardControlKey, Search } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  FormControlLabel,
  InputAdornment,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Box, Stack } from '@mui/system';
import moment from 'moment';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { usePopover } from 'src/hooks/use-popover';
import {
  applySelectedTemplateAction,
  getAllTherapyPlanningTemplates,
  resetGetAllTherapyTemplates,
} from 'src/redux/actions/therapyPlanning';
import { WarningIconTherapyTemplate } from './commonComponent/therapy-planning-icons';
import { BasicDateCalendar } from './therapy-planning-template-2.0';
import _ from 'lodash';

type Props = {
  therapyType: string;
  close: any;
  dischargeDate: any;
  getTherapyTemplateCell: any;
};

const TherapyPlanningTemplateApply = (props: Props) => {
  const dispatch = useDispatch();
  const { therapyType, close, getTherapyTemplateCell, dischargeDate } = props;
  const selectedPatient = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);
  const getAllTemplatesList = useSelector(
    (state: any) => state.getAllTherapyPlaningTemplatesReducer
  );

  const applySelectedTherapyTemplate = useSelector(
    (state: any) => state.applySelectedTherapyPlanningTemplateReducer
  );

  const [openCalender, setOpenCalender] = useState(false);
  const [selectedDate, setSelectedDate] = useState<any>(null);
  const calenderRef = useRef<HTMLDivElement | null>(null);

  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  const [dischargeCheckBox, setDischargeCheckBox] = useState<boolean>(false);

  const handleValueChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDischargeCheckBox(event.target.checked);
  };

  useEffect(() => {
    if (openCalender) {
      const handleClickOutside = (event: any) => {
        if (calenderRef.current && !calenderRef.current.contains(event.target)) {
          handleClickCalender();
        }
      };

      document.addEventListener('click', handleClickOutside);

      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [openCalender]);

  const handleClickCalender = () => {
    setOpenCalender((prev: any) => !prev);
  };
  const handleChangeDate = (e: any) => {
    const newDate = moment(e);
    setSelectedDate(newDate.toDate());
    setOpenCalender(false);
  };

  function getNextWeekDate(date: any) {
    return moment(date).add(1, 'week').format('YYYY-MM-DD');
  }

  const handleSaveTherapyTemplate = () => {
    const currentDate = moment(new Date()).format('YYYY-MM-DD')
    dispatch(
      applySelectedTemplateAction({
        patientId: selectedPatient?.uuid,
        params: {
          setToMax: dischargeCheckBox,
          discipline: therapyType,
          templateId: selectedTemplate?.uuid,
          startDate: getTherapyTemplateCell?.date < currentDate ? currentDate : getTherapyTemplateCell?.date,
          toDate: dischargeCheckBox ? null : moment(selectedDate).format('YYYY-MM-DD'),
        },
      })
    );
  };

  useEffect(() => {
    if (
      !applySelectedTherapyTemplate?.isTemplateApply &&
      applySelectedTherapyTemplate?.response === null
    )
      return;
    if (
      applySelectedTherapyTemplate?.isTemplateApply &&
      applySelectedTherapyTemplate?.response !== null
    ) {
      close();
    }
    if (
      !applySelectedTherapyTemplate?.isTemplateApply &&
      applySelectedTherapyTemplate?.response !== null
    ) {
      close();
    }
  }, [applySelectedTherapyTemplate]);

  const getNinthDay = (currentDate: any) => {
    const particularDate = moment(currentDate).startOf('day').add(89, 'days');
    return particularDate.format('YYYY-MM-DD');
  };

  const inputRef = useRef<HTMLInputElement>(null);
  const popover = usePopover<HTMLButtonElement>();
  const containerRef = useRef<HTMLDivElement>(null);

  const handleOpenPopover = () => {
    popover.handleOpen();
  };

  const handleScroll = () => {
    if (!containerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10 && !getAllTemplatesList?.loading) {
      if (
        getAllTemplatesList?.response?.data?.totalElements !==
        getAllTemplatesList?.totalTemplates?.length
      ) {
        dispatch(
          getAllTherapyPlanningTemplates({
            params: {
              page: getAllTemplatesList?.pageNumber,
              pageSize: 20,
            },
          })
        );
      }
    }
  };
  const handleSelectTemplates = (template: any, index: number) => {
    setSelectedTemplate(template);
    popover.handleClose();
  };

  const [searchTerm, setSearchTerm] = useState('');

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };
  const avoidBubblingUp = (e: React.KeyboardEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    const debounceSearch = _.debounce(() => {
      dispatch(resetGetAllTherapyTemplates());
      dispatch(
        getAllTherapyPlanningTemplates({
          params: {
            page: 0,
            pageSize: 20,
            templateName: searchTerm?.trim(),
          },
        })
      );
    }, 1000);

    debounceSearch();

    return () => debounceSearch.cancel();
  }, [searchTerm]);

  return (
    <>
      <Box p={1}>
        <>
          <DialogContent>
            <Stack
              flexDirection={'column'}
              rowGap={2}
            >
              <Stack
                sx={{ display: 'flex' }}
                mb={3}
              >
                <Typography variant="h5">Select a template </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    marginTop: 1.5,
                  }}
                >
                  The template will be applied starting the selected week
                </Typography>
              </Stack>
              <Stack
                direction={'row'}
                sx={{ width: '100%' }}
                justifyContent={'space-between'}
              >
                <Stack
                  sx={{
                    width: '48%',
                  }}
                >
                  <Button
                    sx={{
                      borderRadius: '12px',
                      border: '1px solid rgba(56, 66, 80, 0.50)',
                      padding: '6px 16px',
                      color: '#384250',
                      fontSize: '14px',
                      fontWeight: 400,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                    onClick={handleOpenPopover}
                    ref={popover.anchorRef}
                    endIcon={popover?.open ? <KeyboardControlKey /> : <KeyboardArrowDown />}
                  >
                    {selectedTemplate?.templateName
                      ? selectedTemplate?.templateName
                      : 'Template Name'}
                  </Button>
                  <Menu
                    anchorEl={popover.anchorRef.current}
                    onClose={popover.handleClose}
                    open={popover.open}
                    PaperProps={{
                      sx: {
                        width: 270,
                        '& .MuiMenuItem-root + .MuiDivider-root': {
                          marginTop: '10px',
                        },
                      },
                    }}
                  >
                    <TextField
                      variant="outlined"
                      placeholder={`Search by Template Name`}
                      fullWidth
                      onChange={handleSearchInputChange}
                      value={searchTerm}
                      sx={{
                        p: 1,
                        pt: 0,
                        '& .MuiInputBase-input': {
                          padding: '12px 0px',
                        },
                      }}
                      inputRef={inputRef}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search />
                          </InputAdornment>
                        ),
                      }}
                      onKeyDown={avoidBubblingUp}
                    />
                    <Box
                      ref={containerRef}
                      onScroll={handleScroll}
                      style={{
                        height: '300px',
                        overflowY: 'auto',
                      }}
                    >
                      {!getAllTemplatesList?.totalTemplates?.length &&
                      !getAllTemplatesList?.loading ? (
                        <Typography
                          color={'text.secondary'}
                          variant="subtitle2"
                          textAlign={'center'}
                          mt={2}
                        >
                          No results found
                        </Typography>
                      ) : (
                        getAllTemplatesList?.totalTemplates?.map((option: any, index: number) => (
                          <MenuItem
                            onClick={() => handleSelectTemplates(option, index)}
                            key={option?.uuid}
                            sx={{
                              overflow: 'hidden',
                              backgroundColor:
                                selectedPatient?.uuid === option?.uuid
                                  ? 'rgb(206 207 255 / 50%)'
                                  : 'none',
                            }}
                            title={`${option?.templateName}`}
                          >{`${option?.templateName}`}</MenuItem>
                        ))
                      )}

                      {getAllTemplatesList?.loading && (
                        <Box
                          display="flex"
                          justifyContent="center"
                          p={2}
                        >
                          <CircularProgress />
                        </Box>
                      )}
                    </Box>
                  </Menu>
                </Stack>
                <Stack
                  ref={calenderRef}
                  sx={{
                    position: 'relative',
                    width: '48%',
                  }}
                >
                  <Button
                    sx={{
                      borderRadius: '12px',
                      border: '1px solid rgba(56, 66, 80, 0.50)',
                      padding: '6px 16px',
                      color: '#384250',
                      fontSize: '14px',
                      fontWeight: 400,
                      display: 'flex',
                      justifyContent: 'space-between',
                    }}
                    onClick={handleClickCalender}
                    disabled={dischargeCheckBox}
                    endIcon={openCalender ? <KeyboardControlKey /> : <KeyboardArrowDown />}
                  >
                    To {selectedDate ? `(${moment(selectedDate).format('MMM D, YYYY')})` : null}
                  </Button>
                  {openCalender && (
                    <BasicDateCalendar
                      value={selectedDate === null ? new Date() : selectedDate}
                      handleChangeDate={handleChangeDate}
                      minDate={new Date(getTherapyTemplateCell?.date)}
                      maxDate={
                        dischargeDate
                          ? new Date(dischargeDate)
                          : new Date(getNinthDay(new Date(getTherapyTemplateCell?.date)))
                      }
                    />
                  )}
                </Stack>
              </Stack>
              <Stack>
                <FormControlLabel
                  sx={{ zIndex: 8 }}
                  control={
                    <Checkbox
                      onChange={handleValueChange}
                      value={dischargeCheckBox}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        textWrap: 'nowrap',
                        overflow: 'hidden',
                      }}
                    >
                      Set to Max. (Either the Anticipated DC or up to 90 days)
                    </Typography>
                  }
                />
              </Stack>
              <Stack
                flexDirection={'row'}
                gap={2}
              >
                <Tooltip
                  placement="bottom-start"
                  title={`Applying the template will override existing planned ${therapyType} minutes`}
                >
                  <Box
                    sx={{
                      borderRadius: '10px',
                      background: 'rgba(99, 102, 241, 0.20)',
                      width: '30px',
                      height: '30px',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      cursor: 'pointer',
                    }}
                  >
                    <WarningIconTherapyTemplate />
                  </Box>
                </Tooltip>
                <Typography>
                  Note: Applying the template will override existing planned minutes
                </Typography>
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button
              autoFocus
              variant="text"
              sx={{ color: '#111927' }}
              onClick={close}
            >
              Cancel
            </Button>
            <LoadingButton
              autoFocus
              variant="contained"
              onClick={handleSaveTherapyTemplate}
              loading={applySelectedTherapyTemplate?.loading}
              disabled={(!selectedDate && !dischargeCheckBox) || !selectedTemplate?.uuid}
            >
              Apply Template
            </LoadingButton>
          </DialogActions>
        </>
      </Box>
    </>
  );
};

export default TherapyPlanningTemplateApply;
